/**
 * Inpatient encounters in ChartMind: the setting choice, the Events list, the Daily Plan
 * comments, and what all of it adds to the note request.
 *
 * Ported from ChartMind-7 (HealthDeskMinusOne), cut down to one set of instructions for every
 * specialty. Outpatient encounters never touch anything in here.
 */

import {
  buildTestsForNoteContext,
  buildTreatmentsForNoteContext,
} from "./plan2Catalog";

export const ENCOUNTER_SETTINGS = {
  OUTPATIENT: "outpatient",
  INPATIENT: "inpatient",
};

export const isInpatient = (setting) => setting === ENCOUNTER_SETTINGS.INPATIENT;

/**
 * Inpatient versions of ChartMind's prompts, by base prompt id. Created per environment by
 * scripts/seedInpatientPrompts.js; an environment without them falls back to the base prompt.
 */
export const INPATIENT_PROMPT_IDS = {
  "chartmind-diagnosis": "chartmind-diagnosis-inpatient",
  "chartmind-treatment": "chartmind-treatment-inpatient",
  "chartmind-discharge": "chartmind-discharge-inpatient",
  "chartmind-notes": "chartmind-notes-inpatient",
  "chartmind-pearls": "chartmind-pearls-inpatient",
};

/** Prompt options for a step: the inpatient prompt with the base as fallback, or nothing. */
export const promptForSetting = (basePromptId, setting) =>
  isInpatient(setting)
    ? { promptId: INPATIENT_PROMPT_IDS[basePromptId], fallbackPromptId: basePromptId }
    : {};

/** Unknown or missing values read as outpatient, which is how every older session was made. */
export const normalizeEncounterSetting = (value) =>
  isInpatient(value) ? ENCOUNTER_SETTINGS.INPATIENT : ENCOUNTER_SETTINGS.OUTPATIENT;

// ============================================================================
// Events
// ============================================================================

/** Suggested inpatient event names, A–Z. Clinicians can still type a name that is not listed. */
export const INPATIENT_EVENT_OPTIONS = [
  "Acute Bleeding",
  "Acute Pain",
  "Agitation",
  "Altered Mental Status",
  "Arrhythmia",
  "Aspiration",
  "Bradycardia",
  "Chest Pain",
  "Code Blue",
  "Delirium",
  "Fall",
  "Fever",
  "Hypertension",
  "Hypoglycemic Episode",
  "Hypotension",
  "Hypoxia",
  "Nausea and Vomiting",
  "Oliguria",
  "Rapid Response",
  "Respiratory Distress",
  "Seizure",
  "Shortness of Breath",
  "Syncope",
  "Tachycardia",
  "Transfusion Reaction",
  "Unresponsiveness",
];

/**
 * Events are only ever entered by the clinician. They carry no time: they are all entered in one
 * sitting, usually long after they happened, so the time of entry would chart a wrong time. When
 * the time matters the clinician puts it in the comment.
 */
export const createInpatientEvent = (name, comment = "") => ({
  // eslint-disable-next-line no-restricted-properties -- Non-security: local list-item id.
  id: `event-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  name: String(name || "").trim(),
  comment: String(comment || ""),
});

export const normalizeInpatientEvents = (value) =>
  (Array.isArray(value) ? value : [])
    .map((row) => ({
      id: String(row?.id || "").trim(),
      name: String(row?.name || "").trim(),
      comment: String(row?.comment || ""),
    }))
    .filter((row) => row.id && row.name);

// ============================================================================
// Daily Plan
// ============================================================================

/**
 * A checked item is pending when the clinician has neither marked it reassuring nor written
 * anything about it. A comment that says the item is still open ("due Tuesday") is left to the
 * model, which is told to list those under Pending as well.
 */
export const isPendingDailyPlanItem = (item, { comments = {}, reassuring = {} } = {}) =>
  !reassuring[item.id] && !String(comments[item.id] || "").trim();

/** Names of the checked items still pending. */
export const pendingDailyPlanNames = (checkedItems = [], marks = {}) =>
  checkedItems.filter((item) => isPendingDailyPlanItem(item, marks)).map((item) => item.name);

/**
 * The note's Pending section must list every pending Daily Plan item. The model is told to, and
 * sometimes drops one anyway, so any it left out are added back after the note comes in.
 * Anything else it put there (a blood culture mentioned only in the narrative) is kept.
 */
const normalizeLine = (line) =>
  line.replace(/^[\s\-*•\d.)]+/, "").trim().toLowerCase();

/** True when some line of the section starts with the item's name (then ends or punctuation). */
const sectionLists = (lines, name) => {
  const target = name.trim().toLowerCase();
  return lines.some(
    (line) => line === target || (line.startsWith(target) && /^[^a-z0-9]/.test(line.slice(target.length))),
  );
};

export const ensurePendingInNote = (sections, pendingNames = []) => {
  if (!sections || !pendingNames.length) return sections;
  const current = String(sections.pending || "").trim();
  // Line by line, not anywhere in the text: "Chest X-ray" is not listed just because
  // "Repeat chest X-ray" is.
  const lines = current.split("\n").map(normalizeLine).filter(Boolean);
  const missing = pendingNames.filter((name) => !sectionLists(lines, name));
  if (!missing.length) return sections;
  const added = missing.map((name) => `- ${name}`).join("\n");
  return { ...sections, pending: current ? `${current}\n${added}` : added };
};

const dailyPlanLine = (item, { comments = {}, reassuring = {} }) => {
  const extra = [reassuring[item.id] ? "reassuring" : "", String(comments[item.id] || "").trim()]
    .filter(Boolean)
    .join("; ");
  return `- ${item.name}${extra ? `: ${extra}` : ""}`;
};

// ============================================================================
// Note
// ============================================================================

/**
 * Inpatient notes are an admission H&P, a next-day progress note, or an Events note for
 * documented inpatient events. Outpatient has no type.
 */
export const NOTE_FORMATS = {
  ADMISSION: "admission",
  PROGRESS: "progress",
  EVENTS: "events",
};

/** The Events note chart prompt. QueryManager edits this document; there is no fallback. */
export const EVENTS_NOTE_PROMPT_ID = "chartmind-notes-events";

/** True when the inpatient note type is the Events flow. */
export const isEventsNoteFormat = (value) => value === NOTE_FORMATS.EVENTS;

/**
 * Unknown or missing values read as admission, which is how every older session was made.
 * `events` and `progress` are preserved.
 */
export const normalizeNoteFormat = (value) => {
  if (value === NOTE_FORMATS.PROGRESS) return NOTE_FORMATS.PROGRESS;
  if (value === NOTE_FORMATS.EVENTS) return NOTE_FORMATS.EVENTS;
  return NOTE_FORMATS.ADMISSION;
};

/**
 * Events note: the clinician-entered events are the clinical source, the way a narrative
 * transcript is for the other note types. Long enough on its own for the 50-character gates
 * the diagnosis and plan hooks still apply.
 */
export const buildEventsSourceText = (events = []) => {
  const eventRows = normalizeInpatientEvents(events);
  if (!eventRows.length) return "";
  const lines = eventRows.map((event) => {
    const comment = event.comment.trim();
    return `- ${event.name}${comment ? `: ${comment}` : ""}`;
  });
  return [
    "EVENTS DOCUMENTED BY THE PHYSICIAN:",
    "These events are the clinical source for this encounter. There is no separate narrative transcript.",
    "",
    ...lines,
  ].join("\n");
};

/**
 * Appended to diagnostic-test and treatment requests for the Events note type so those lists
 * stay on the events and do not become an admission workup or a hospital daily plan.
 */
export const EVENTS_PLAN_FOCUS = [
  "EVENTS NOTE FOCUS:",
  "Every physical exam finding, diagnostic test, and treatment must address the events the physician documented above.",
  "Do not expand into a full admission workup or a hospital daily plan.",
].join("\n");

/**
 * Starting shape for the Events chart. SOAP mapped onto events: S = event names,
 * O = event comments + kept exam findings, A = differential, P = studies + treatments.
 * The live section list comes from chartmind-notes-events in QueryManager; this is
 * the seed until a response arrives, and the preferred key order when keys return.
 */
export const EVENTS_NOTE_TEMPLATE = {
  id: "chartmind_events_note",
  name: "Event note",
  description: "SOAP note for inpatient events the physician documented",
  guidance:
    "Subjective is the event name(s). Objective is bullet events with comments plus kept exam findings. Assessment is the differential. Plan lists diagnostic studies and treatments.",
  sections: [
    {
      key: "subjective",
      title: "Subjective",
      required: true,
      placeholder: "Event name(s) the physician documented",
    },
    {
      key: "objective",
      title: "Objective",
      required: true,
      placeholder: "Event comments and exam findings as bullets",
    },
    {
      key: "assessment",
      title: "Assessment",
      required: true,
      placeholder: "Differential diagnosis",
    },
    {
      key: "plan",
      title: "Plan",
      required: true,
      placeholder: "Diagnostic studies and treatments",
    },
  ],
};

/**
 * Format one exam line for Objective / EXAM context.
 * Undocumented → exam name only. Documented → "Name: Normal" or "Name: finding".
 *
 * @param {{ name?: string }} exam
 * @param {{ status?: string, finding?: string }|null|undefined} result
 */
export const formatExamObjectiveLine = (exam, result) => {
  const name = String(exam?.name || "").trim();
  if (!name) return "";
  if (!result || !result.status) return `- ${name}`;
  if (result.status === "normal") return `- ${name}: Normal`;
  const finding = String(result.finding || "").trim();
  return finding ? `- ${name}: ${finding}` : `- ${name}`;
};

/**
 * Events Objective section text: event+comment bullets, then exam documentation.
 * Exam 2 mode uses systems rows; Exam mode uses kept examFindings + examResults.
 *
 * @param {Object} params
 * @param {Array} params.events
 * @param {Array} params.examFindings  kept exams only (Exam mode)
 * @param {Map|Record<string, {status?: string, finding?: string}>} [params.examResults]
 * @param {"exam"|"exam2"} [params.examMode]
 * @param {Array<{name?: string, value?: string}>} [params.exam2Systems]
 */
export const buildEventsObjectiveText = ({
  events = [],
  examFindings = [],
  examResults = null,
  examMode = "exam",
  exam2Systems = [],
} = {}) => {
  const lines = [];

  normalizeInpatientEvents(events).forEach((event) => {
    const comment = event.comment.trim();
    lines.push(`- ${event.name}${comment ? `: ${comment}` : ""}`);
  });

  if (examMode === "exam2") {
    (Array.isArray(exam2Systems) ? exam2Systems : []).forEach((system) => {
      const name = String(system?.name || "").trim();
      const values = Array.isArray(system?.value)
        ? system.value.map((item) => String(item || "").trim()).filter(Boolean)
        : String(system?.value || "")
            .split(/\s*;\s*/)
            .map((item) => item.trim())
            .filter(Boolean);
      if (name && values.length) lines.push(`- ${name}: ${values.join("; ")}`);
    });
    return lines.join("\n");
  }

  const resultsMap =
    examResults instanceof Map
      ? examResults
      : examResults && typeof examResults === "object"
        ? new Map(Object.entries(examResults))
        : new Map();

  examFindings.forEach((exam) => {
    const line = formatExamObjectiveLine(exam, resultsMap.get(exam.id));
    if (line) lines.push(line);
  });

  return lines.join("\n");
};

/**
 * Events note request context: the events plus the chosen exams, tests and treatments.
 * No Daily Plan or Pending block.
 */
export const buildEventsNoteContext = ({
  events = [],
  examFindings = [],
  examResults = null,
  examMode = "exam",
  exam2Systems = [],
  tests = [],
  treatments = [],
  /** When set with plan2ByCategory, branches per category list|table mode. */
  allTests = null,
  plan2ByCategory = null,
  disabledTestIds = null,
  testResults = null,
  allTreatments = null,
  treatment2ByCategory = null,
  selectedTreatmentIds = null,
  disabledTreatmentIds = null,
} = {}) => {
  const blocks = [];

  const eventRows = normalizeInpatientEvents(events);
  if (eventRows.length) {
    const lines = eventRows.map((event) => {
      const comment = event.comment.trim();
      return `- ${event.name}${comment ? `: ${comment}` : ""}`;
    });
    blocks.push(`EVENTS:\n${lines.join("\n")}`);
  }

  if (examMode === "exam2") {
    const lines = (Array.isArray(exam2Systems) ? exam2Systems : [])
      .map((system) => {
        const name = String(system?.name || "").trim();
        const values = Array.isArray(system?.value)
          ? system.value.map((item) => String(item || "").trim()).filter(Boolean)
          : String(system?.value || "")
              .split(/\s*;\s*/)
              .map((item) => item.trim())
              .filter(Boolean);
        return name && values.length ? `- ${name}: ${values.join("; ")}` : "";
      })
      .filter(Boolean);
    if (lines.length) blocks.push(`EXAM:\n${lines.join("\n")}`);
  } else if (examFindings.length) {
    const resultsMap =
      examResults instanceof Map
        ? examResults
        : examResults && typeof examResults === "object"
          ? new Map(Object.entries(examResults))
          : new Map();
    const lines = examFindings
      .map((finding) => formatExamObjectiveLine(finding, resultsMap.get(finding.id)))
      .filter(Boolean);
    if (lines.length) blocks.push(`EXAM:\n${lines.join("\n")}`);
  }

  if (allTests != null) {
    const lines = buildTestsForNoteContext({
      allTests,
      plan2ByCategory,
      disabledTestIds: disabledTestIds || new Set(),
      testResults,
    });
    if (lines.length) blocks.push(`DIAGNOSTIC TESTS:\n${lines.join("\n")}`);
  } else if (tests.length) {
    const lines = tests.map((t) => {
      const result = String(t.result || "").trim();
      return `- ${t.name}${result ? `: Result: ${result}` : ""}`;
    });
    blocks.push(`DIAGNOSTIC TESTS:\n${lines.join("\n")}`);
  }

  if (allTreatments != null) {
    const lines = buildTreatmentsForNoteContext({
      allTreatments,
      treatment2ByCategory,
      selectedTreatmentIds,
      disabledTreatmentIds: disabledTreatmentIds || new Set(),
    });
    if (lines.length) blocks.push(`TREATMENTS:\n${lines.join("\n")}`);
  } else if (treatments.length) {
    // Names only — the Events chart prompt writes treatments as a bullet list without
    // category/route commentary.
    const lines = treatments.map((t) => `- ${t.name}`);
    blocks.push(`TREATMENTS:\n${lines.join("\n")}`);
  }

  return blocks.join("\n\n");
};

/**
 * Sent only when the note falls back to the base prompt because chartmind-notes-inpatient is not
 * set up yet. Once it is, these instructions live in that prompt and the Query Manager.
 */
export const INPATIENT_FALLBACK_INSTRUCTIONS = [
  "INPATIENT NOTE INSTRUCTIONS:",
  "This is an inpatient encounter. Write the note the DOCUMENTATION OUTLINE names, not a clinic visit note. The transcript above is the physician's narrative.",
  "Use only what the physician provided. Do not add diagnoses, orders, or events that are not listed.",
  "DAILY PLAN lists only the items the physician checked. Write every one of them in the assessment and plan, with its result or comment when the physician entered one: a comment after a colon goes with the item, and a line that says reassuring means the physician found it reassuring. Do not drop an item or its comment.",
  "PENDING lists checked items that are still open. Put every one of them in the pending section. Also put there any Daily Plan item whose comment says it is still outstanding, for example \"due Tuesday\" or \"sent to outside lab\".",
  "EVENTS lists events the physician documented. Document each one in the hospital course, or in the interval history of a progress note. Use a time only when the physician's comment gives one.",
  "Organize the assessment and plan by problem.",
  "The SECTIONS line is the contract: return exactly those keys, even where an earlier JSON schema in this prompt names different ones.",
].join("\n");

/**
 * Exam + Diagnostics documentation for note generation (shared by Events and
 * Admission/Progress / outpatient when those steps are filled).
 */
export const buildExamDiagnosticsNoteContext = ({
  examFindings = [],
  examResults = null,
  examMode = "exam",
  exam2Systems = [],
  allTests = null,
  plan2ByCategory = null,
  disabledTestIds = null,
  testResults = null,
} = {}) => {
  const blocks = [];

  if (examMode === "exam2") {
    const lines = (Array.isArray(exam2Systems) ? exam2Systems : [])
      .map((system) => {
        const name = String(system?.name || "").trim();
        const values = Array.isArray(system?.value)
          ? system.value.map((item) => String(item || "").trim()).filter(Boolean)
          : String(system?.value || "")
              .split(/\s*;\s*/)
              .map((item) => item.trim())
              .filter(Boolean);
        return name && values.length ? `- ${name}: ${values.join("; ")}` : "";
      })
      .filter(Boolean);
    if (lines.length) blocks.push(`EXAM:\n${lines.join("\n")}`);
  } else if (examFindings.length) {
    const resultsMap =
      examResults instanceof Map
        ? examResults
        : examResults && typeof examResults === "object"
          ? new Map(Object.entries(examResults))
          : new Map();
    const lines = examFindings
      .map((finding) => formatExamObjectiveLine(finding, resultsMap.get(finding.id)))
      .filter(Boolean);
    if (lines.length) blocks.push(`EXAM:\n${lines.join("\n")}`);
  }

  if (allTests != null) {
    const lines = buildTestsForNoteContext({
      allTests,
      plan2ByCategory,
      disabledTestIds: disabledTestIds || new Set(),
      testResults,
    });
    if (lines.length) blocks.push(`DIAGNOSTIC TESTS:\n${lines.join("\n")}`);
  }

  return blocks.join("\n\n");
};

/**
 * The Daily Plan, Pending and Events for the note request, as one block of text. Empty lists are
 * left out rather than sent as empty headings. Optional Exam / Diagnostics *2 blocks mirror Events.
 *
 * @param {Object} params
 * @param {Array<{id: string, name: string}>} params.checkedItems - Daily Plan items checked
 * @param {Object<string, string>} params.comments - comment by item id
 * @param {Object<string, boolean>} params.reassuring - reassuring mark by item id
 * @param {Array} params.events - clinician-entered events
 */
export const buildInpatientNoteContext = ({
  checkedItems = [],
  comments = {},
  reassuring = {},
  events = [],
  examFindings = [],
  examResults = null,
  examMode = "exam",
  exam2Systems = [],
  allTests = null,
  plan2ByCategory = null,
  disabledTestIds = null,
  testResults = null,
} = {}) => {
  const marks = { comments, reassuring };
  const blocks = [];

  const examDiagnostics = buildExamDiagnosticsNoteContext({
    examFindings,
    examResults,
    examMode,
    exam2Systems,
    allTests,
    plan2ByCategory,
    disabledTestIds,
    testResults,
  });
  if (examDiagnostics) blocks.push(examDiagnostics);

  if (checkedItems.length) {
    blocks.push(`DAILY PLAN:\n${checkedItems.map((item) => dailyPlanLine(item, marks)).join("\n")}`);
  }

  const pending = pendingDailyPlanNames(checkedItems, marks);
  if (pending.length) {
    blocks.push(`PENDING:\n${pending.map((name) => `- ${name}`).join("\n")}`);
  }

  const eventRows = normalizeInpatientEvents(events);
  if (eventRows.length) {
    const lines = eventRows.map((event) => {
      const comment = event.comment.trim();
      return `- ${event.name}${comment ? `: ${comment}` : ""}`;
    });
    blocks.push(`EVENTS:\n${lines.join("\n")}`);
  }

  return blocks.join("\n\n");
};
